import _typeof from '@babel/runtime/helpers/typeof';
import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import _extends from '@babel/runtime/helpers/extends';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import classnames from 'classnames';
import React__default, { useState, useRef, useEffect, useCallback } from 'react';
import alertUserOfDeprecatedProps from '../utils/deprecation.js';
import safeEscape from '../utils/safeEscape.js';
import Overlay from './Overlay.js';

var _excluded = ["as", "className", "disabled", "selected", "to"],
  _excluded2 = ["as", "children", "className", "isVisible", "onRequestClose", "location", "size"],
  _excluded3 = ["as", "children", "className", "location", "onRequestClose", "size", "isVisible"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

/*
  Dropdown.MenuItem Component
*/
var DropdownMenuItem = function DropdownMenuItem(_ref) {
  var _ref$as = _ref.as,
    As = _ref$as === void 0 ? 'button' : _ref$as,
    className = _ref.className,
    disabled = _ref.disabled,
    selected = _ref.selected,
    to = _ref.to,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var finalClasses = classnames('C-DropdownMenuItem__item', {
    isDisabled: disabled,
    isSelected: selected
  }, className);
  var props = _objectSpread(_objectSpread({
    className: finalClasses,
    disabled: disabled
  }, to && {
    to: to
  }), otherProps);
  return /*#__PURE__*/React__default.createElement("li", {
    className: "C-DropdownMenuItem"
  }, /*#__PURE__*/React__default.createElement(As, props));
};
DropdownMenuItem.displayName = 'Dropdown.MenuItem';

/*
  Dropdown.Menu
*/
var DropdownMenu = function DropdownMenu(_ref2) {
  var _ref2$as = _ref2.as,
    As = _ref2$as === void 0 ? 'div' : _ref2$as,
    children = _ref2.children,
    className = _ref2.className,
    isVisible = _ref2.isVisible,
    onRequestClose = _ref2.onRequestClose,
    _ref2$location = _ref2.location,
    location = _ref2$location === void 0 ? 'bottom-left' : _ref2$location,
    _ref2$size = _ref2.size,
    size = _ref2$size === void 0 ? 'normal' : _ref2$size,
    otherProps = _objectWithoutProperties(_ref2, _excluded2);
  alertUserOfDeprecatedProps('DropdownMenu', otherProps);
  var finalClasses = classnames('C-DropdownMenu', _defineProperty(_defineProperty({
    isVisible: isVisible
  }, "--size-".concat(size), size), "--location-".concat(location), location), className);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(As, _extends({
    className: finalClasses
  }, otherProps, {
    onClick: onRequestClose
  }), children), /*#__PURE__*/React__default.createElement(Overlay, {
    onClick: onRequestClose,
    isVisible: isVisible
  }));
};
DropdownMenu.displayName = 'Dropdown.Menu';

/*
  Dropdown Component
*/
var Dropdown = function Dropdown(_ref3) {
  var _ref3$as = _ref3.as,
    As = _ref3$as === void 0 ? 'div' : _ref3$as,
    children = _ref3.children,
    className = _ref3.className,
    location = _ref3.location,
    propsOnRequestClose = _ref3.onRequestClose,
    size = _ref3.size,
    _ref3$isVisible = _ref3.isVisible,
    propsIsVisible = _ref3$isVisible === void 0 ? false : _ref3$isVisible,
    otherProps = _objectWithoutProperties(_ref3, _excluded3);
  alertUserOfDeprecatedProps('Dropdown', otherProps);
  var _useState = useState(propsIsVisible || false),
    _useState2 = _slicedToArray(_useState, 2),
    isVisible = _useState2[0],
    setIsVisible = _useState2[1];
  var isInitialMount = useRef(true);
  useEffect(function () {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else {
      // Runs after component has mounted and initialized & allows for override of internal state by passed in prop
      setIsVisible(propsIsVisible);
    }
  }, [propsIsVisible]);
  var handleRequestClose = function handleRequestClose(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsVisible(false);
  };
  var onRequestClose = propsOnRequestClose || handleRequestClose;
  var onEscape = useCallback(function (event) {
    return safeEscape(onRequestClose, isVisible)(event);
  }, [onRequestClose, isVisible]);
  useEffect(function () {
    window.addEventListener('keyup', onEscape);
    return function () {
      // returned function will be called on component unmount
      window.removeEventListener('keyup', onEscape);
    };
  }, [isVisible, onEscape]);
  var onRequestOpen = function onRequestOpen(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsVisible(true);
  };
  var finalClasses = classnames('C-Dropdown', className);
  if (!Array.isArray(children) || children.length !== 2 || children[0].type.displayName !== 'Dropdown.Menu' && children[1].type.displayName !== 'Dropdown.Menu') {
    console.error('Dropdown requires DropdownMenu and an open target.');
  }
  var dropdownMenuChild;
  var targetChild;
  if (children[0].type === DropdownMenu) {
    dropdownMenuChild = children[0];
    targetChild = children[1];
  } else {
    dropdownMenuChild = children[1];
    targetChild = children[0];
  }
  var focusedItem;
  var index = 0;
  var disableClass;
  var onKey = useCallback(function (event) {
    var _event$view;
    var element = (_event$view = event.view) === null || _event$view === void 0 ? void 0 : _event$view.document;
    var len = (element === null || element === void 0 ? void 0 : element.getElementsByTagName('li').length) - 2;

    //Key down
    if (event.keyCode === 40) {
      var next;
      var _ul = element.querySelector('ul.C-DropdownMenu');
      if (index + 2 <= len) {
        index++;
      }
      if (focusedItem) {
        var _next, _disableClass, _focusedItem;
        next = _ul === null || _ul === void 0 ? void 0 : _ul.getElementsByTagName('li')[index];
        disableClass = (_next = next) === null || _next === void 0 ? void 0 : _next.querySelector('.C-DropdownMenuItem__item').className;
        if ((_disableClass = disableClass) !== null && _disableClass !== void 0 && _disableClass.includes('isDisabled')) {
          index++;
          next = _ul === null || _ul === void 0 ? void 0 : _ul.getElementsByTagName('li')[index];
        }
        if (_typeof(next) !== undefined && index + 1 <= len) {
          focusedItem = next;
        } else {
          if (index + 1 === len) {
            index = len;
            focusedItem = _ul.querySelector('.C-DropdownMenuItem__item');
          }
        }
        (_focusedItem = focusedItem) === null || _focusedItem === void 0 ? void 0 : _focusedItem.querySelector('.C-DropdownMenuItem__item').focus();
      } else {
        var _focusedItem2;
        index = 0;
        focusedItem = _ul.querySelector('.C-DropdownMenuItem__item');
        (_focusedItem2 = focusedItem) === null || _focusedItem2 === void 0 ? void 0 : _focusedItem2.focus();
      }
    }

    //Key up
    if (event.keyCode === 38) {
      var prev;
      var ul = element.querySelector('ul.C-DropdownMenu');
      index--;
      if (focusedItem) {
        var _prev, _disableClass2, _focusedItem3;
        prev = ul === null || ul === void 0 ? void 0 : ul.getElementsByTagName('li')[index];
        disableClass = (_prev = prev) === null || _prev === void 0 ? void 0 : _prev.querySelector('.C-DropdownMenuItem__item').className;
        if ((_disableClass2 = disableClass) !== null && _disableClass2 !== void 0 && _disableClass2.includes('isDisabled')) {
          index--;
          prev = ul === null || ul === void 0 ? void 0 : ul.getElementsByTagName('li')[index];
        }
        if (_typeof(prev) !== undefined && index <= len) {
          focusedItem = prev;
        } else {
          index = 0;
          focusedItem = ul.querySelector('.C-DropdownMenuItem__item.isSelected');
        }
        (_focusedItem3 = focusedItem) === null || _focusedItem3 === void 0 ? void 0 : _focusedItem3.querySelector('.C-DropdownMenuItem__item').focus();
      } else {
        var _focusedItem4;
        index = 0;
        focusedItem = ul.querySelector('.C-DropdownMenuItem__item.isSelected');
        (_focusedItem4 = focusedItem) === null || _focusedItem4 === void 0 ? void 0 : _focusedItem4.querySelector('.C-DropdownMenuItem__item').focus();
      }
    }
    return;
  }, []);
  useEffect(function () {
    window.addEventListener('keydown', onKey);
    return function () {
      // returned function will be called on component unmount
      window.removeEventListener('keydown', onKey);
    };
  });
  dropdownMenuChild = /*#__PURE__*/React__default.cloneElement(dropdownMenuChild, {
    as: 'ul',
    isVisible: isVisible,
    location: location,
    onRequestClose: onRequestClose,
    size: size
  });
  targetChild = /*#__PURE__*/React__default.cloneElement(targetChild, {
    onClick: targetChild.props.onClick || onRequestOpen
  });
  return /*#__PURE__*/React__default.createElement(As, _extends({
    className: finalClasses,
    "aria-expanded": isVisible
  }, otherProps), targetChild, dropdownMenuChild);
};
Dropdown.displayName = 'Dropdown';
Dropdown.Menu = DropdownMenu;
Dropdown.MenuItem = DropdownMenuItem;

export { Dropdown, DropdownMenu, DropdownMenuItem, Dropdown as default };
